import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FileText, ShieldCheck, Upload } from "lucide-react";
import { z } from "zod";

import { StepFooter } from "@/features/onboarding/ui/IdentityStep";
import {
  useUpdateWorkerProfile,
  useUploadEligibilityDoc,
} from "@/features/workers/model/useWorkerProfile";
import {
  LEGAL_STATUS_LABELS,
  type LegalStatus,
  type WorkerProfile,
} from "@/features/workers/types";
import { cn } from "@/shared/lib/cn";
import {
  ALLOWED_DOC_TYPES,
  DOC_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

const OPTIONS: LegalStatus[] = ["CITIZEN", "PERMANENT_RESIDENT", "OPEN_WORK_PERMIT"];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Exactly 9 digits — no formatting because the input is fully masked,
// so dashes the user can't see add no value and just complicate validation.
const SIN_DIGITS_RE = /^\d{9}$/;
const stripNonDigits = (v: string) => v.replace(/\D/g, "").slice(0, 9);

function buildSchema(sinAlreadyProvided: boolean) {
  return z
    .object({
      legal_status: z.enum(["CITIZEN", "PERMANENT_RESIDENT", "OPEN_WORK_PERMIT"], {
        errorMap: () => ({ message: "Pick the option that applies to you." }),
      }),
      work_permit_expires_on: z.string().optional(),
      // SIN is required on first capture, optional on re-edit (server still
      // has it encrypted). When provided it must be exactly 9 digits.
      sin: z.string(),
    })
    .superRefine((values, ctx) => {
      const sinDigits = stripNonDigits(values.sin);
      if (!sinDigits) {
        if (!sinAlreadyProvided) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["sin"],
            message: "SIN is required.",
          });
        }
      } else if (!SIN_DIGITS_RE.test(sinDigits)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sin"],
          message: "Enter all 9 digits of your SIN.",
        });
      }
      if (values.legal_status === "OPEN_WORK_PERMIT") {
        if (!values.work_permit_expires_on) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["work_permit_expires_on"],
            message: "Permit expiry date is required.",
          });
        } else if (values.work_permit_expires_on <= todayIso()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["work_permit_expires_on"],
            message: "Permit expiry must be in the future.",
          });
        }
      }
    });
}

type FormValues = {
  legal_status: "CITIZEN" | "PERMANENT_RESIDENT" | "OPEN_WORK_PERMIT";
  work_permit_expires_on?: string;
  sin: string;
};

interface Props {
  profile: WorkerProfile;
  onSaved: () => void;
  onBack: () => void;
}

export function EligibilityStep({ profile, onSaved, onBack }: Props) {
  const update = useUpdateWorkerProfile();
  const uploadDoc = useUploadEligibilityDoc();
  const docInputRef = useRef<HTMLInputElement>(null);
  const [docError, setDocError] = useState<string | null>(null);

  const schema = buildSchema(profile.sin_provided);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      legal_status: (profile.legal_status as LegalStatus | null) ?? undefined,
      work_permit_expires_on: profile.work_permit_expires_on ?? "",
      // SIN never round-trips from the server — even encrypted, no part of
      // it is ever sent back. Field always starts empty; `sin_provided`
      // tells the user whether one is on file.
      sin: "",
    },
  });

  const selected = watch("legal_status");

  const onDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_DOC_TYPES);
    if (err) {
      setDocError(err);
      return;
    }
    setDocError(null);
    uploadDoc.mutate(file);
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!profile.eligibility_document_url) {
      setError("root", {
        type: "manual",
        message: "Upload your ID document before continuing.",
      });
      return;
    }
    clearErrors("root");
    const sinDigits = stripNonDigits(values.sin);
    // Skip the sin field on re-edit if the worker left it blank — the
    // backend already has the encrypted value and we don't want to clear it.
    const payload: {
      legal_status: typeof values.legal_status;
      work_permit_expires_on: string | null;
      sin?: string;
    } = {
      legal_status: values.legal_status,
      work_permit_expires_on:
        values.legal_status === "OPEN_WORK_PERMIT"
          ? values.work_permit_expires_on || null
          : null,
    };
    if (sinDigits) payload.sin = sinDigits;
    await update.mutateAsync(payload);
    onSaved();
  });

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-display-tight text-copy">
          Work eligibility in Canada
        </h2>
        <p className="mt-1 text-sm text-copy-muted">
          Required by Canadian employment law. Your selection and documents are shared
          with our ops team during verification.
        </p>
      </div>

      <div className="rounded-md border border-info bg-info-light p-3 text-xs text-info-foreground">
        <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
        Stored securely. We share with venues only what they need to confirm a hire.
      </div>

      <div className="space-y-2">
        <Controller
          control={control}
          name="legal_status"
          render={({ field }) => (
            <>
              {OPTIONS.map((opt) => (
                <label
                  key={opt}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-md border bg-surface p-3 transition-colors",
                    field.value === opt
                      ? "border-primary ring-2 ring-primary ring-offset-1"
                      : "border-border hover:bg-surface-sunken",
                  )}
                >
                  <input
                    type="radio"
                    name={field.name}
                    value={opt}
                    checked={field.value === opt}
                    onChange={() => field.onChange(opt)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium text-copy">
                    {LEGAL_STATUS_LABELS[opt]}
                  </span>
                </label>
              ))}
            </>
          )}
        />
        {errors.legal_status && (
          <p className="text-xs text-destructive">{errors.legal_status.message}</p>
        )}
      </div>

      {selected === "OPEN_WORK_PERMIT" && (
        <div className="space-y-1">
          <label
            htmlFor="work_permit_expires_on"
            className="text-sm font-medium text-copy"
          >
            Permit expiry date
          </label>
          <input
            id="work_permit_expires_on"
            type="date"
            min={todayIso()}
            {...register("work_permit_expires_on")}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.work_permit_expires_on && (
            <p className="text-xs text-destructive">
              {errors.work_permit_expires_on.message}
            </p>
          )}
          <p className="text-xs text-copy-muted">
            We'll remind you before your permit expires so your account stays active.
          </p>
        </div>
      )}

      <div className="space-y-1">
        <label htmlFor="sin" className="text-sm font-medium text-copy">
          Social Insurance Number (SIN)
        </label>
        <Controller
          name="sin"
          control={control}
          render={({ field }) => (
            <input
              id="sin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={9}
              placeholder="9 digits"
              value={field.value}
              onChange={(e) => {
                // Strip non-digits aggressively so paste of "123-456-789"
                // still works, even though the user can't see the result.
                setValue("sin", stripNonDigits(e.target.value), {
                  shouldValidate: false,
                });
              }}
              onBlur={field.onBlur}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm tracking-wider tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
            />
          )}
        />
        {errors.sin && <p className="text-xs text-destructive">{errors.sin.message}</p>}
        <p className="text-xs text-copy-muted">
          Encrypted at rest and never displayed again — not even the last four
          digits. Required for payroll and your background check.
          {profile.sin_provided && (
            <>
              {" "}
              <span className="font-medium text-copy">
                Already on file. Leave blank to keep the saved value.
              </span>
            </>
          )}
        </p>
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-copy">
          ID document <span className="text-xs font-normal text-copy-subtle">(required)</span>
        </h3>
        <p className="text-xs text-copy-muted">
          Government-issued ID that proves your right to work — PR card,
          citizenship certificate, or work permit. PDF or image, ≤5 MB.
        </p>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken text-copy-subtle">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            {profile.eligibility_document_url ? (
              <a
                href={profile.eligibility_document_url}
                target="_blank"
                rel="noreferrer"
                className="block truncate text-sm font-medium text-primary hover:underline"
                title={profile.eligibility_document_url}
              >
                Uploaded — view
              </a>
            ) : (
              <p className="text-sm text-copy-muted">No document uploaded yet</p>
            )}
            {profile.eligibility_document_uploaded_at && (
              <p className="text-xs text-copy-subtle">
                Uploaded{" "}
                {new Date(profile.eligibility_document_uploaded_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => docInputRef.current?.click()}
            disabled={uploadDoc.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
          >
            <Upload className="h-4 w-4" />
            {uploadDoc.isPending
              ? "Uploading…"
              : profile.eligibility_document_url
                ? "Replace"
                : "Upload"}
          </button>
          <input
            ref={docInputRef}
            type="file"
            accept={DOC_ACCEPT_ATTR}
            onChange={onDocChange}
            className="hidden"
          />
        </div>
        {docError && <p className="text-xs text-destructive-foreground">{docError}</p>}
        {uploadDoc.isError && (
          <p className="text-xs text-destructive-foreground">
            {(uploadDoc.error as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? "Upload failed. Try a smaller file or different format."}
          </p>
        )}
      </section>

      {errors.root?.message && (
        <p className="text-xs text-destructive">{errors.root.message}</p>
      )}

      <StepFooter
        onBack={onBack}
        isSubmitting={isSubmitting || update.isPending}
        nextDisabled={!profile.eligibility_document_url}
      />
    </form>
  );
}
